import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";
import RequestRideModal from "./RequestRideModal";

export default function UberFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [requestingRide, setRequestingRide] = useState(null);

  const serviceTypes = [
    { id: "all", name: "Todos" },
    { id: "ride", name: "Corridas" },
    { id: "delivery", name: "Entregas" },
    { id: "freight", name: "Fretes" },
    { id: "moto", name: "Moto" },
  ];

  const vehicleTypes = {
    economy: "Econômico",
    comfort: "Conforto",
    premium: "Premium",
    xl: "XL",
    moto: "Moto",
    truck: "Caminhão",
  };

  const featureIcons = {
    ac: "❄️",
    wifi: "📶",
    music: "🎵",
    child_seat: "👶",
    pet_friendly: "🐾",
    wheelchair: "♿",
    luggage_space: "🧳",
  };

  const featureNames = {
    ac: "Ar Condicionado",
    wifi: "Wi-Fi",
    music: "Sistema de Som",
    child_seat: "Cadeirinha",
    pet_friendly: "Aceita Pets",
    wheelchair: "Acessibilidade",
    luggage_space: "Espaço para Bagagem",
  };

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/uber?page=${pageNum}&limit=12`;
      if (serviceType !== "all") url += `&serviceType=${serviceType}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching uber:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [serviceType]);

  const handleEditPost = (post) => {
    setSelectedDriver(null);
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
    setSelectedDriver(null);
  };

  const handleRequestRide = (post) => {
    setRequestingRide(post);
    setSelectedDriver(null);
  };

  const handleRideSuccess = (ride) => {
    navigate("/profile/me?tab=uber-rides");
  };

  if (loading && posts.length === 0) {
    return <div className="uber_feed_loading"><div className="uber_loader"></div><span>Carregando motoristas...</span></div>;
  }

  return (
    <div className="uber_feed">
      {serviceTypes.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {serviceTypes.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${serviceType === filter.id ? "active" : ""}`}
                onClick={() => setServiceType(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="uber_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>
          <h3>Nenhum motorista encontrado</h3>
          <p>Cadastre-se como motorista!</p>
        </div>
      ) : (
        <div className="uber_list">
          {posts.map((post) => (
            <div key={post._id} className="uber_card" onClick={() => setSelectedDriver(post)}>
              <div className="uber_card_top">
                <div className="uber_card_left">
                  <img src={post.user.picture} alt="" className="uber_driver_photo" />
                  <div className="uber_driver_info">
                    <h3>{post.user.first_name} {post.user.last_name}</h3>
                    <div className="uber_rating">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                      </svg>
                      <span>{(post.uberData?.rating || 5).toFixed(1)}</span>
                      <span className="uber_trips">• {post.uberData?.totalTrips || 0} viagens</span>
                    </div>
                    {post.uberData?.yearsOfExperience > 0 && (
                      <span className="uber_experience">{post.uberData.yearsOfExperience} anos de experiência</span>
                    )}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                  />
                </div>
              </div>

              <div className="uber_card_vehicle">
                <div className="uber_vehicle_main">
                  <span className="uber_vehicle_type">{vehicleTypes[post.uberData?.vehicleType] || "Veículo"}</span>
                  <span className="uber_vehicle_model">
                    {post.uberData?.vehicleBrand} {post.uberData?.vehicleModel} {post.uberData?.vehicleYear}
                  </span>
                  <span className="uber_vehicle_color">{post.uberData?.vehicleColor}</span>
                </div>
                {post.uberData?.features && post.uberData.features.length > 0 && (
                  <div className="uber_features">
                    {post.uberData.features.slice(0, 4).map((feature, i) => (
                      <span key={i} className="uber_feature" title={featureNames[feature]}>
                        {featureIcons[feature]}
                      </span>
                    ))}
                    {post.uberData.features.length > 4 && (
                      <span className="uber_feature_more">+{post.uberData.features.length - 4}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="uber_card_services">
                {post.uberData?.services?.map((s, i) => (
                  <span key={i} className="uber_service_badge">
                    {serviceTypes.find(t => t.id === s)?.name || s}
                  </span>
                ))}
              </div>

              <div className="uber_card_location">
                📍 {post.uberData?.city}{post.uberData?.state ? `, ${post.uberData.state}` : ""}
              </div>

              <div className="uber_card_bottom">
                <div className="uber_price">
                  <span className="uber_price_label">A partir de</span>
                  <span className="uber_price_value">R$ {(post.uberData?.baseFare || 0).toFixed(2)}</span>
                </div>
                {post.uberData?.available && (
                  <span className="uber_available">● Disponível agora</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="uber_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedDriver && (
        <div className="uber_modal_overlay" onClick={() => setSelectedDriver(null)}>
          <div className="uber_modal" onClick={(e) => e.stopPropagation()}>
            <button className="uber_modal_close" onClick={() => setSelectedDriver(null)}>✕</button>

            <div className="uber_modal_header">
              <img src={selectedDriver.user.picture} alt="" className="uber_modal_photo" />
              <div className="uber_modal_driver_info">
                <h2>{selectedDriver.user.first_name} {selectedDriver.user.last_name}</h2>
                <div className="uber_modal_rating">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                  <span>{(selectedDriver.uberData?.rating || 5).toFixed(1)}</span>
                  <span>• {selectedDriver.uberData?.totalTrips || 0} viagens</span>
                  {selectedDriver.uberData?.yearsOfExperience > 0 && (
                    <span>• {selectedDriver.uberData.yearsOfExperience} anos de experiência</span>
                  )}
                </div>
              </div>
            </div>

            <div className="uber_modal_body">
              <div className="uber_modal_vehicle">
                <h4>🚗 Veículo</h4>
                <p className="vehicle_name">
                  {selectedDriver.uberData?.vehicleBrand} {selectedDriver.uberData?.vehicleModel}
                </p>
                <div className="vehicle_details">
                  <span>{selectedDriver.uberData?.vehicleYear}</span>
                  <span>•</span>
                  <span>{selectedDriver.uberData?.vehicleColor}</span>
                  <span>•</span>
                  <span className="vehicle_plate">{selectedDriver.uberData?.vehiclePlate}</span>
                </div>
                {selectedDriver.uberData?.vehicleCapacity && (
                  <p className="vehicle_capacity">
                    Capacidade: {selectedDriver.uberData.vehicleCapacity} passageiros
                  </p>
                )}
              </div>

              {selectedDriver.uberData?.features && selectedDriver.uberData.features.length > 0 && (
                <div className="uber_modal_features">
                  <h4>✨ Recursos</h4>
                  <div className="features_grid">
                    {selectedDriver.uberData.features.map((feature, i) => (
                      <div key={i} className="feature_badge">
                        <span className="feature_icon">{featureIcons[feature]}</span>
                        <span className="feature_name">{featureNames[feature]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="uber_modal_services">
                <h4>🎯 Serviços oferecidos</h4>
                <div className="uber_services_list">
                  {selectedDriver.uberData?.services?.map((s, i) => (
                    <span key={i}>{serviceTypes.find(t => t.id === s)?.name || s}</span>
                  ))}
                </div>
              </div>

              <div className="uber_modal_coverage">
                <h4>📍 Área de cobertura</h4>
                <p className="coverage_city">
                  {selectedDriver.uberData?.city}{selectedDriver.uberData?.state ? `, ${selectedDriver.uberData.state}` : ""}
                </p>
                {selectedDriver.uberData?.coverage && (
                  <p className="coverage_description">{selectedDriver.uberData.coverage}</p>
                )}
                {selectedDriver.uberData?.coverageRadius && (
                  <p className="coverage_radius">Raio de {selectedDriver.uberData.coverageRadius} km</p>
                )}
              </div>

              {selectedDriver.uberData?.description && (
                <div className="uber_modal_about">
                  <h4>📝 Sobre</h4>
                  <p>{selectedDriver.uberData.description}</p>
                </div>
              )}

              <div className="uber_modal_pricing">
                <h4>💰 Tarifas</h4>
                <div className="uber_pricing_grid">
                  <div className="uber_pricing_item">
                    <span>Tarifa base</span>
                    <strong>R$ {(selectedDriver.uberData?.baseFare || 0).toFixed(2)}</strong>
                  </div>
                  <div className="uber_pricing_item">
                    <span>Por km</span>
                    <strong>R$ {(selectedDriver.uberData?.pricePerKm || 0).toFixed(2)}</strong>
                  </div>
                  <div className="uber_pricing_item">
                    <span>Por minuto</span>
                    <strong>R$ {(selectedDriver.uberData?.pricePerMin || 0).toFixed(2)}</strong>
                  </div>
                </div>
                {selectedDriver.uberData?.minimumFare && (
                  <p className="minimum_fare">Corrida mínima: R$ {selectedDriver.uberData.minimumFare.toFixed(2)}</p>
                )}
              </div>
            </div>

            <div className="uber_modal_footer">
              <button 
                className="uber_request_btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestRide(selectedDriver);
                }}
              >
                🚗 Solicitar Corrida
              </button>
              <Link 
                to={`/profile/${selectedDriver.user.username}`} 
                className="uber_profile_btn"
                onClick={(e) => e.stopPropagation()}
              >
                Ver perfil
              </Link>
            </div>
          </div>
        </div>
      )}

      {requestingRide && (
        <RequestRideModal
          driver={requestingRide.user}
          driverPost={requestingRide}
          user={user}
          onClose={() => setRequestingRide(null)}
          onSuccess={handleRideSuccess}
        />
      )}

      {hasMore && (
        <div className="uber_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedDriver && (
        <div className="uber_modal_overlay" onClick={() => setSelectedDriver(null)}>
          <div className="uber_modal" onClick={(e) => e.stopPropagation()}>
            <button className="uber_modal_close" onClick={() => setSelectedDriver(null)}>✕</button>

            <div className="uber_modal_header">
              <img src={selectedDriver.user.picture} alt="" className="uber_modal_photo" />
              <div className="uber_modal_driver_info">
                <h2>{selectedDriver.user.first_name} {selectedDriver.user.last_name}</h2>
                <div className="uber_modal_rating">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                  <span>{(selectedDriver.uberData?.rating || 5).toFixed(1)}</span>
                  <span>• {selectedDriver.uberData?.totalTrips || 0} viagens</span>
                </div>
              </div>
            </div>

            <div className="uber_modal_body">
              <div className="uber_modal_vehicle">
                <h4>Veículo</h4>
                <p>{selectedDriver.uberData?.vehicleModel} • {selectedDriver.uberData?.vehicleColor}</p>
                <p className="uber_plate">{selectedDriver.uberData?.vehiclePlate}</p>
              </div>

              <div className="uber_modal_services">
                <h4>Serviços oferecidos</h4>
                <div className="uber_services_list">
                  {selectedDriver.uberData?.services?.map((s, i) => (
                    <span key={i}>{serviceTypes.find(t => t.id === s)?.name || s}</span>
                  ))}
                </div>
              </div>

              <div className="uber_modal_coverage">
                <h4>Área de cobertura</h4>
                <p>{selectedDriver.uberData?.coverage || "Toda a cidade"}</p>
              </div>

              {selectedDriver.uberData?.description && (
                <div className="uber_modal_about">
                  <h4>Sobre</h4>
                  <p>{selectedDriver.uberData.description}</p>
                </div>
              )}

              <div className="uber_modal_pricing">
                <h4>Valores</h4>
                <div className="uber_pricing_grid">
                  <div className="uber_pricing_item">
                    <span>Tarifa base</span>
                    <strong>R$ {(selectedDriver.uberData?.baseFare || 0).toFixed(2)}</strong>
                  </div>
                  <div className="uber_pricing_item">
                    <span>Por km</span>
                    <strong>R$ {(selectedDriver.uberData?.pricePerKm || 0).toFixed(2)}</strong>
                  </div>
                  <div className="uber_pricing_item">
                    <span>Por minuto</span>
                    <strong>R$ {(selectedDriver.uberData?.pricePerMin || 0).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="uber_modal_footer">
              <button className="uber_contact_btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                Chamar agora
              </button>
              <Link to={`/profile/${selectedDriver.user.username}`} className="uber_profile_btn">
                Ver perfil
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
